import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import LambdaIcon from '../../../../assets/aws/lambda.svg';
import type { CondorLambda } from 'config-editor-common';
import {
  DEFAULT_SNAPSTART_ENABLED,
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from 'config-editor-common';
import { LambdaNameInputRef } from '../../../LambdaComponents/shared/LambdaNameInput';

// AWS Lambda orange colors (match phx-api LambdaItemEditor)
const LAMBDA_ORANGE = '#FF9900';
const LAMBDA_ORANGE_LIGHT = '#FFB84D';

const useStyles = makeStyles(theme => ({
  lambdaItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${LAMBDA_ORANGE_LIGHT}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[2],
      borderColor: LAMBDA_ORANGE,
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  nameContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  lambdaIcon: {
    width: '1.25rem',
    height: '1.25rem',
    flexShrink: 0,
  },
  triggerPathRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  addPathButton: {
    marginTop: theme.spacing(0.5),
  },
}));

interface CondorLambdaItemEditorProps {
  name: string;
  lambda: CondorLambda;
  onUpdate: (name: string, field: keyof CondorLambda, value: unknown) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
}

/**
 * Normalize the stored lambda_trigger_path (string | string[] | undefined) into an
 * array of strings for editing. A missing value yields a single empty input.
 */
function triggerPathsToArray(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.length > 0 ? [...value] : [''];
  if (typeof value === 'string') return [value];
  return [''];
}

/**
 * Normalize an array of trigger paths back to storage form: a single non-empty
 * value is stored as a string, multiple as a string[]. Empty inputs are dropped;
 * if all are empty, an empty string is stored.
 */
function arrayToTriggerPaths(paths: string[]): string | string[] {
  const cleaned = paths.map(p => p.trim()).filter(Boolean);
  if (cleaned.length === 0) return '';
  if (cleaned.length === 1) return cleaned[0];
  return cleaned;
}

export const CondorLambdaItemEditor = forwardRef<LambdaNameInputRef, CondorLambdaItemEditorProps>(
  ({ name, lambda, onUpdate, onRename, onDelete }, ref) => {
    const classes = useStyles();
    const [nameInput, setNameInput] = useState(name);
    const nameInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        requestAnimationFrame(() => {
          nameInputRef.current?.focus();
          nameInputRef.current?.select();
        });
      },
    }));

    const handleRename = () => {
      if (nameInput.trim() && nameInput !== name) {
        onRename(name, nameInput.trim());
      }
    };

    // Trigger paths live in local state so a transient empty row can exist while
    // the user fills it in. Storage form (arrayToTriggerPaths) drops empties, so
    // deriving the rows straight from props would discard a just-added blank row
    // before it could render - that was the "Add trigger path does nothing" bug.
    const [triggerPaths, setTriggerPaths] = useState<string[]>(() =>
      triggerPathsToArray(lambda.lambda_trigger_path),
    );

    // Content-based key of the stored value so a parent that recreates an
    // equivalent array (e.g. structuredClone on config round-trip) is not mistaken
    // for an external change.
    const incomingKey = JSON.stringify(
      arrayToTriggerPaths(triggerPathsToArray(lambda.lambda_trigger_path)),
    );

    // Re-sync only when the stored value changes content from something other than
    // our own commit (e.g. a different config is loaded). We track the last value
    // we wrote by content key, so our own writes - and any equal-but-new array a
    // parent republishes - are ignored, keeping the transient empty row alive.
    const lastStoredKeyRef = useRef(incomingKey);
    useEffect(() => {
      if (incomingKey !== lastStoredKeyRef.current) {
        lastStoredKeyRef.current = incomingKey;
        setTriggerPaths(triggerPathsToArray(lambda.lambda_trigger_path));
      }
    }, [incomingKey, lambda.lambda_trigger_path]);

    const commitTriggerPaths = (paths: string[]) => {
      setTriggerPaths(paths);
      const stored = arrayToTriggerPaths(paths);
      lastStoredKeyRef.current = JSON.stringify(stored);
      onUpdate(name, 'lambda_trigger_path', stored);
    };

    const updateTriggerPath = (index: number, value: string) => {
      const next = [...triggerPaths];
      next[index] = value;
      commitTriggerPaths(next);
    };

    const addTriggerPath = () => {
      commitTriggerPaths([...triggerPaths, '']);
    };

    const removeTriggerPath = (index: number) => {
      const next = [...triggerPaths];
      next.splice(index, 1);
      commitTriggerPaths(next.length > 0 ? next : ['']);
    };

    return (
      <div className={classes.lambdaItem} data-ai-anchor={`lambda.${name}.settings`}>
        <div className={classes.headerRow}>
          <div className={classes.nameContainer}>
            <img src={LambdaIcon} alt="Lambda" className={classes.lambdaIcon} />
            <TextField
              inputRef={nameInputRef}
              label="Lambda Name"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onBlur={handleRename}
              fullWidth
              variant="outlined"
              size="small"
            />
          </div>
          <Tooltip title="Delete Lambda">
            <IconButton
              size="small"
              onClick={() => onDelete(name)}
              color="secondary"
              tabIndex={-1}
              data-ai-anchor={`lambda.${name}.delete`}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Lambda Handler"
              value={lambda.lambda_handler || ''}
              onChange={e => onUpdate(name, 'lambda_handler', e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g., lambda_function.lambda_handler"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Lambda Trigger Path(s)
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginBottom: 8 }}
            >
              API path pattern(s), e.g. */GET/events. Add multiple to route several paths to this
              lambda (dual-route).
            </Typography>
            {triggerPaths.map((path, i) => (
              <Box className={classes.triggerPathRow} key={`trigger-path-${i}`}>
                <TextField
                  label={`Trigger Path ${triggerPaths.length > 1 ? i + 1 : ''}`.trim()}
                  value={path}
                  onChange={e => updateTriggerPath(i, e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="e.g., */GET/events"
                />
                <Tooltip title="Remove path">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => removeTriggerPath(i)}
                      color="secondary"
                      tabIndex={-1}
                      disabled={triggerPaths.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addTriggerPath}
              className={classes.addPathButton}
            >
              Add trigger path
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Timeout (s)"
              type="number"
              value={lambda.timeout ?? ''}
              onChange={e =>
                onUpdate(name, 'timeout', e.target.value ? parseInt(e.target.value, 10) : undefined)
              }
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Override default"
              inputProps={{ ...LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Memory (MB)"
              type="number"
              value={lambda.memory_size ?? ''}
              onChange={e =>
                onUpdate(
                  name,
                  'memory_size',
                  e.target.value ? parseInt(e.target.value, 10) : undefined,
                )
              }
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Override default"
              inputProps={{ ...LAMBDA_MEMORY_MB_INPUT_PROPS }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lambda.snapstart ?? DEFAULT_SNAPSTART_ENABLED}
                  onChange={e => onUpdate(name, 'snapstart', e.target.checked)}
                  color="primary"
                />
              }
              label="SnapStart"
            />
          </Grid>
        </Grid>
      </div>
    );
  },
);

CondorLambdaItemEditor.displayName = 'CondorLambdaItemEditor';
